import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpService } from '@app/services/http.service';
import { environment } from '@env/environment';
import { Role } from '../../../models/role';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { Code } from '@app/models/code';
import { takeUntil, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AppComponent } from '@app/app.component';
import { PageTitleService } from '@app/services/page-title.service';
import { MatDialog } from '@angular/material/dialog';
import { LoaderService } from '@app/services/loader.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from '@app/base.component';
import { SnackbarService } from '@app/services/snackbar.service';
import { RouteDataService } from '@app/services/route-data.service';

@Component({
selector: 'app-code-edit',
templateUrl: './code-edit.component.html',
styleUrls: ['./code-edit.component.css']
})
export class CodeEditComponent extends BaseComponent implements OnInit {

// variables
private isLoaded: boolean = false;
isRegister: boolean = true;
subTitle: string = "Edit Code";
hidePassword: boolean = true;
activeRoles: Role[];
urlBack: string = null;
urlBackDelete: string = null;
codeId: number = null;
code: Code = null;

// code form
codeForm = new UntypedFormGroup({
  code: new UntypedFormControl( null, { validators: [Validators.required, Validators.pattern('^[0-9A-Z.]*$')] }),
  code_order: new UntypedFormControl( null, { validators: [Validators.required, Validators.pattern('^[0-9]*$')] }),
  description: new UntypedFormControl( null, {validators: [Validators.required] }),
  description_long: new UntypedFormControl( null, {validators: [Validators.required] }),
  code_id: new UntypedFormControl( null, {}),
  is_active: new UntypedFormControl( null, {}),
  code_type: new UntypedFormControl( null, { validators: Validators.required })
});

// form field data
codeTypes: Array<{id: string, name: string}> = [ 
  { id: 'CPT', name: 'CPT' },
  { id: 'ICD-10', name: 'ICD-10' }
];

constructor(
  public dialogDelete: MatDialog,
  private httpService: HttpService,
  private router: Router,
  private activatedRoute: ActivatedRoute,
  private appComponent: AppComponent,
  private pageTitleService: PageTitleService,
  private loaderService: LoaderService,
  private snackbarService: SnackbarService, 
  private routeDataService: RouteDataService
) {
  super();
  this.urlBack = this.router.createUrlTree(['/administration/codes']).toString();
  this.urlBackDelete = this.router.createUrlTree(['/administration/codes']).toString();
  this.activatedRoute.params.subscribe(params => {
    if (params.code_id) {
      this.codeId = params.code_id;
      this.isRegister = false;
      this.subTitle = "Edit Code"
    }

    const urlApi = this.httpService.createUrl([
      'api',
      'roles'
    ], {}, { host: environment.host_api_default });

    this.httpService.get<Code[]>(urlApi).subscribe((data: Code[]) => {

      this.fetchCode(this.codeId).pipe(
        tap( _ => null )
      ).subscribe(data => {
        this.code = data;
        this.codeForm.patchValue({ code_id: data.code_id });
        this.codeForm.patchValue({ code: data.code });
        this.codeForm.patchValue({ code_order: data.code_order });
        this.codeForm.patchValue({ code_type: data.code_type });
        this.codeForm.patchValue({ description: data.description });
        this.codeForm.patchValue({ is_active: data.is_active });
        this.codeForm.patchValue({ description_long: data.description_long });
      }, () => {
        // do nothing
      }, () => {
        this.isLoaded = true;
      });
    });
  });
}

handleBack(): void {
  this.router.navigateByUrl(this.urlBack);
}

isFormLoaded(): boolean {
  return this.isLoaded;
}
// handle whether or not submit button should be active
getIsSubmitActive(): boolean {
  return !this.codeForm.valid;
}

// handle form submit
handleSubmit(): void {
  if (this.codeForm.valid) {
    this.loaderService.show();
    this.updateCode();
  }
}

// when delete button is clicked
handleButtonDelete(): void {
  const dialogReference = this.dialogDelete.open( CodeEditDialogDeleteComponent );
  dialogReference.afterClosed()
  .pipe( takeUntil( this.appUnsubscribe ) )
  .subscribe({
    next: ( r ) => {
      if( r )
      {
        this.handleDelete();
      }
    }
  }); 
}


// delete current visit
private handleDelete(): void {

  let urlApiCode: string = this.httpService.createUrl([ 
    'api', 
    'codes',
    this.code.code_id
  ],
    {},
    { host: environment.host_api_default }
  );

  this.httpService.delete( urlApiCode )
  .pipe(
    tap( _ => { 
      this.loaderService.show();
    })
  ).subscribe({
    next: () => {
      this.snackbarService.show(`Code deleted successfully`);
      this.router.navigateByUrl( this.urlBackDelete );
    },
    error: ( e ) => {
      // get actual response from server
      const errorResponse = e.error || e.message;
      this.snackbarService.show(`Error deleting code: ${errorResponse.message || errorResponse}`);
      this.loaderService.hide();
    },
    complete: () => {
      this.loaderService.hide();
    }
  });
}


updateCode(): void {
  const urlApi = this.httpService.createUrl([
    'api',
    'codes',
    this.codeId
  ], {}, { host: environment.host_api_default});

  let update = this.httpService.put(urlApi, this.codeForm.value);
  update.subscribe({
    next: (code) => {
      this.loaderService.hide();

      this.routeDataService.loadAndCache<Code>('codes', ['api', 'codes'], { is_active: true }).subscribe();

      const urlCodeView = this.router.createUrlTree([
        'administration',
        'codes',
        this.codeId
      ]);

      this.router.navigateByUrl( urlCodeView );
    }, error: (e) => {
      this.loaderService.hide();
      this.openSnackBar(`Error updating code: ${e.error.message}`);
    }
  });
}

fetchCode(code_id: number): Observable<Code> {
  let urlApi: string = this.httpService.createUrl([
    'api',
    'codes',
    code_id
  ], {}, { host: environment.host_api_default });

  return this.httpService.get<Code>(urlApi);
}

ngOnInit(): void {
  this.pageTitleService.setPageTitle('Code Administration');
  this.appComponent.appendSiteTitle('administration', 'codes', 'edit')
}

openSnackBar(toolTip: string) {
  this.snackbarService.show(toolTip);
}

}



// dialog specific to this component
@Component({
selector: 'app-code-edit-dialog-delete',
templateUrl: 'code-edit-dialog-delete.html'
})
export class CodeEditDialogDeleteComponent {
}